import { ClipboardList } from 'lucide-react'
import { GRACKLE_COLORS } from '@/lib/theme'
import { WorksheetScreen } from '@/components/training/WorksheetScreen'
import type { SheetColumn } from '@/components/training/SheetTable'
import type { TrainingWorksheetRow, TrainingVocabularies } from '@/types'

/**
 * The "GracklePDF Onboarding" tab.
 *
 * The widest of the five: an hours budget, a pre-meeting, and up to four support
 * options, each with its own support description, notes, trainer and status. The
 * sheet has those four groups as sixteen side-by-side columns and this keeps that
 * shape, because a client can genuinely be mid-option-2 while option 4 is untouched.
 *
 * Two columns are text on purpose, and their headers say so. In the source,
 * "Pre-Meeting Date" holds a date in only 13 of 134 rows — the rest are notes like
 * "No meeting necessary" — and "Remaining OnBoarding Hours" reads "30 min
 * pre-meeting plus 1 hour" seven times. Coercing either would delete what it says.
 */

// The three the sheet offers; "Cancelled" was invented here and no row holds it.
const OPTION_TINTS: Record<string, string> = {
  Completed: GRACKLE_COLORS.pistachio,
  'In Progress': GRACKLE_COLORS.pollen,
  'Not Started': GRACKLE_COLORS.powderBlue,
}

const columns = (vocab?: TrainingVocabularies): SheetColumn[] => {
  const statuses = vocab?.onboarding_option_status ?? []
  const supportOptions = vocab?.onboarding_support_option ?? []
  const optionGroup = (n: number): SheetColumn[] => [
    {
      // A dropdown of nine named support options in the sheet, free text here until
      // migration 126. The sheet's four columns offer slightly different lists; this
      // is their union, including the sheet's own "Documenht" typo, which 7 rows hold.
      key: `option_${n}_support`,
      label: `Option ${n} support`,
      kind: 'select',
      options: supportOptions,
      width: 'min-w-[20rem]',
    },
    {
      key: `option_${n}_notes`,
      label: `Option ${n} notes`,
      kind: 'longtext',
      width: 'min-w-[18rem]',
    },
    {
      key: `option_${n}_trainer`,
      label: `Option ${n} trainer`,
      kind: 'text',
      width: 'min-w-[11rem]',
    },
    {
      key: `option_${n}_status`,
      label: `Option ${n} status`,
      kind: 'select',
      options: statuses,
      width: 'min-w-[10rem]',
    },
  ]

  return [
    { key: 'account_name', label: 'CRM account', kind: 'readonly', width: 'min-w-[15rem]' },
    { key: 'client_name', label: 'Client', kind: 'text', width: 'min-w-[15rem]' },
    { key: 'poc_name', label: 'POC name', kind: 'text', width: 'min-w-[11rem]' },
    { key: 'poc_email', label: 'POC email', kind: 'text', width: 'min-w-[14rem]' },
    { key: 'sales_rep_name', label: 'Sales rep', kind: 'text', width: 'min-w-[11rem]' },
    {
      key: 'remaining_hours_text',
      label: 'Remaining onboarding hours',
      kind: 'text',
      width: 'min-w-[13rem]',
      note: 'free text in the sheet',
    },
    {
      key: 'pre_meeting_trainer',
      label: 'Pre-meeting trainer',
      kind: 'text',
      width: 'min-w-[11rem]',
    },
    {
      key: 'pre_meeting_date_text',
      label: 'Pre-meeting date',
      kind: 'text',
      width: 'min-w-[14rem]',
      note: 'free text — mostly notes, not dates',
    },
    {
      key: 'pre_meeting_completed',
      label: 'Pre-meeting completed',
      kind: 'select',
      options: vocab?.yes_no ?? [],
      width: 'min-w-[10rem]',
    },
    ...[1, 2, 3, 4].flatMap(optionGroup),
  ]
}

export default function TrainingOnboarding() {
  return (
    <WorksheetScreen
      sheet="onboarding"
      title="PDF Onboarding"
      description="Every column from the GracklePDF Onboarding tab, including all four support options. Two columns are deliberately free text — their headers say which."
      icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
      columns={columns}
      tints={OPTION_TINTS}
      tintFor={(r: TrainingWorksheetRow) => OPTION_TINTS[String(r.option_1_status ?? '')]}
      rowLabel={(r) => String(r.client_name ?? r.account_name ?? 'this client')}
      caption="GracklePDF onboarding by client, with the pre-meeting and all four support options. Rows are tinted by option 1 status; the colour key is below the table."
      searchPlaceholder="Search client, point of contact or notes..."
    />
  )
}
